import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContextType';
import apiClient from '../services/api';
import { PROMPT_CATALOG, type PromptGroup, type PromptEntry } from '../configs/promptsCatalog';

/* ── types ─────────────────────────────────────────────────────────────────── */
interface PromptMeta { filename: string; size_bytes: number; }

/* ── styles ─────────────────────────────────────────────────────────────────── */
const S = {
  page:    { display: 'flex', height: '100%', fontFamily: 'inherit', fontSize: '0.82rem', color: '#343a40' } as React.CSSProperties,
  sidebar: { width: 188, flexShrink: 0, borderRight: '1px solid #e9ecef', overflowY: 'auto' as const, padding: '10px 0', background: '#fafafa' } as React.CSSProperties,
  main:    { flex: 1, overflowY: 'auto' as const, padding: '18px 24px' } as React.CSSProperties,

  groupBtn: (active: boolean): React.CSSProperties => ({
    width: '100%', textAlign: 'left', padding: '5px 14px',
    border: 'none', background: active ? '#e7f5ff' : 'transparent',
    color: active ? '#1971c2' : '#495057',
    fontWeight: active ? 600 : 400,
    fontSize: '0.79rem', cursor: 'pointer', borderLeft: `2px solid ${active ? '#1971c2' : 'transparent'}`,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  }),
  countBadge: (color: string): React.CSSProperties => ({
    background: color + '22', color, borderRadius: 8,
    padding: '0 6px', fontSize: '0.68rem', fontWeight: 600, flexShrink: 0,
  }),
  groupHeader: (): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
  }),
  colorDot: (color: string): React.CSSProperties => ({
    width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0,
  }),
  groupTitle: { fontWeight: 700, fontSize: '0.9rem', color: '#212529' } as React.CSSProperties,

  table: { width: '100%', borderCollapse: 'collapse' as const, marginBottom: 6 } as React.CSSProperties,
  th: { padding: '5px 8px', textAlign: 'left' as const, fontWeight: 600, color: '#6c757d', fontSize: '0.74rem', borderBottom: '1px solid #e9ecef', background: '#f8f9fa' } as React.CSSProperties,
  td: { padding: '5px 8px', borderBottom: '1px solid #f1f3f5', verticalAlign: 'top' as const } as React.CSSProperties,
  fileCell: { fontFamily: 'monospace', fontSize: '0.75rem', color: '#343a40' } as React.CSSProperties,
  descCell: { color: '#6c757d', fontSize: '0.78rem' } as React.CSSProperties,
  viewBtn: (active: boolean): React.CSSProperties => ({
    padding: '1px 8px', borderRadius: 3, border: `1px solid ${active ? '#1971c2' : '#ced4da'}`,
    background: active ? '#e7f5ff' : 'white', color: active ? '#1971c2' : '#495057',
    fontSize: '0.72rem', cursor: 'pointer', whiteSpace: 'nowrap',
  }),
  sharedBadge: {
    fontSize: '0.65rem', color: '#868e96', background: '#f1f3f5',
    border: '1px solid #e9ecef', borderRadius: 3, padding: '0 4px', marginLeft: 4,
  } as React.CSSProperties,
  pre: {
    margin: '0 0 12px', padding: '10px 12px',
    background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 4,
    fontSize: '0.72rem', lineHeight: 1.55, whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const, color: '#212529', fontFamily: 'monospace',
    maxHeight: 480, overflowY: 'auto' as const,
  } as React.CSSProperties,
  loadingRow: { padding: '6px 8px', color: '#868e96', fontStyle: 'italic', fontSize: '0.78rem' } as React.CSSProperties,
  errorRow:   { padding: '6px 8px', color: '#e03131', fontSize: '0.78rem' } as React.CSSProperties,
  noAccess:   { padding: 40, textAlign: 'center' as const, color: '#868e96' } as React.CSSProperties,
};

/* ── main component ─────────────────────────────────────────────────────────── */
const AdminPromptsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();

  const [selectedGroupId, setSelectedGroupId] = useState<string>(PROMPT_CATALOG[0].id);
  const [knownFiles, setKnownFiles] = useState<Set<string>>(new Set());
  const [viewingFile, setViewingFile] = useState<string | null>(null);
  const [viewContent, setViewContent] = useState<Record<string, string>>({});
  const [loadingFile, setLoadingFile] = useState<string | null>(null);
  const [errorFiles, setErrorFiles] = useState<Set<string>>(new Set());

  const canView = user?.role === 'SysAdmin' || user?.role === 'DevOps';

  // Jump to group from URL hash (e.g. /admin/prompts#sota from ASB link)
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && PROMPT_CATALOG.some(g => g.id === hash)) setSelectedGroupId(hash);
  }, [location.hash]);

  // Load metadata (file list) once
  useEffect(() => {
    if (!canView) return;
    apiClient.get<PromptMeta[]>('/admin/prompts')
      .then(r => setKnownFiles(new Set(r.data.map(m => m.filename))))
      .catch(() => {/* silently ignore — we still show the catalog */});
  }, [canView]);

  const toggleView = useCallback(async (file: string) => {
    if (viewingFile === file) { setViewingFile(null); return; }
    setViewingFile(file);
    if (viewContent[file] !== undefined) return; // cached
    setLoadingFile(file);
    try {
      const r = await apiClient.get<{ content: string }>(`/admin/prompts/${file}`);
      setViewContent(prev => ({ ...prev, [file]: r.data.content }));
    } catch {
      setErrorFiles(prev => new Set(prev).add(file));
    } finally {
      setLoadingFile(null);
    }
  }, [viewingFile, viewContent]);

  if (!canView) {
    return <div style={S.noAccess}>{t('prompts.admin.noAccess')}</div>;
  }

  const selectedGroup = PROMPT_CATALOG.find(g => g.id === selectedGroupId) ?? PROMPT_CATALOG[0];

  return (
    <div style={S.page}>

      {/* ── Left sidebar: group list ───────────────────────────────── */}
      <aside style={S.sidebar}>
        <div style={{ padding: '4px 14px 8px', fontWeight: 700, fontSize: '0.72rem', color: '#868e96', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          {t('prompts.admin.groups')}
        </div>
        {PROMPT_CATALOG.map(g => (
          <button key={g.id} style={S.groupBtn(g.id === selectedGroupId)} onClick={() => { setSelectedGroupId(g.id); setViewingFile(null); }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.label}</span>
            <span style={S.countBadge(g.color)}>{g.prompts.length}</span>
          </button>
        ))}
      </aside>

      {/* ── Main: selected group prompts ──────────────────────────── */}
      <main style={S.main}>

        {/* Page title — shown once at top of main area */}
        <div style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#212529', margin: 0 }}>
            {t('prompts.admin.title')}
          </h1>
          <p style={{ color: '#868e96', fontSize: '0.78rem', margin: '3px 0 0' }}>
            {t('prompts.admin.subtitle')}
          </p>
        </div>

        <GroupSection
          group={selectedGroup}
          knownFiles={knownFiles}
          viewingFile={viewingFile}
          viewContent={viewContent}
          loadingFile={loadingFile}
          errorFiles={errorFiles}
          onToggleView={toggleView}
        />
      </main>
    </div>
  );
};

/* ── GroupSection ───────────────────────────────────────────────────────────── */
interface GroupSectionProps {
  group: PromptGroup;
  knownFiles: Set<string>;
  viewingFile: string | null;
  viewContent: Record<string, string>;
  loadingFile: string | null;
  errorFiles: Set<string>;
  onToggleView: (file: string) => void;
}

const GroupSection: React.FC<GroupSectionProps> = ({
  group, knownFiles, viewingFile, viewContent, loadingFile, errorFiles, onToggleView,
}) => {
  const { t } = useTranslation();
  return (
  <section id={group.id}>
    <div style={S.groupHeader()}>
      <span style={S.colorDot(group.color)} />
      <span style={S.groupTitle}>{group.label}</span>
      <span style={{ color: '#868e96', fontSize: '0.75rem' }}>
        {group.prompts.length} {t('prompts.admin.promptCount', 'prompts')}
      </span>
    </div>

    <table style={S.table}>
      <thead>
        <tr>
          <th style={S.th}>{t('prompts.admin.colFile')}</th>
          <th style={S.th}>{t('prompts.admin.colDescription')}</th>
          <th style={{ ...S.th, width: 60 }}>{t('prompts.admin.colAction')}</th>
        </tr>
      </thead>
      <tbody>
        {group.prompts.map((p: PromptEntry) => {
          const isViewing = viewingFile === p.file;
          const isLoading = loadingFile === p.file;
          const hasError = errorFiles.has(p.file);
          const missing = knownFiles.size > 0 && !knownFiles.has(p.file);
          return (
            <React.Fragment key={p.file}>
              <tr style={missing ? { opacity: 0.45 } : undefined}>
                <td style={S.td}>
                  <span style={S.fileCell}>{p.file}</span>
                  {p.shared && <span style={S.sharedBadge}>{t('prompts.admin.shared')}</span>}
                  {missing && <span style={{ ...S.sharedBadge, color: '#e03131', borderColor: '#ffc9c9' }}>{t('prompts.admin.missing')}</span>}
                </td>
                <td style={{ ...S.td, ...S.descCell }}>{p.description}</td>
                <td style={S.td}>
                  <button
                    style={S.viewBtn(isViewing)}
                    onClick={() => onToggleView(p.file)}
                    disabled={isLoading || missing}
                  >
                    {isLoading
                      ? t('prompts.admin.loading')
                      : isViewing
                        ? t('prompts.admin.hide')
                        : t('prompts.admin.view')}
                  </button>
                </td>
              </tr>
              {isViewing && (
                <tr>
                  <td colSpan={3} style={{ padding: '0 8px 10px', background: '#fafafa' }}>
                    {hasError
                      ? <div style={S.errorRow}>{t('prompts.admin.loadError')}</div>
                      : viewContent[p.file] !== undefined
                        ? <pre style={S.pre}>{viewContent[p.file]}</pre>
                        : <div style={S.loadingRow}>{t('prompts.admin.loading')}</div>}
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  </section>
  );
};

export default AdminPromptsPage;
